/**
 * Privacy utility functions for masking sensitive user data
 * Consolidates document, email, and general PII masking functions
 */

const CPF_MASK_FORMAT = '000.000.000-00';

/**
 * Masks a document by showing only first 3 and last 3 characters
 * Used for privacy when displaying sensitive document numbers
 *
 * @param document - The document string to mask
 * @returns Masked document (e.g., "123*****789")
 *
 * @example
 * maskDocument("12345678900") // Returns "123*****900"
 */
export function maskDocument(document: string): string {
  if (!document || document.length <= 6) {
    return document; // Return as-is if too short to mask safely
  }

  const firstPart: string = document.substring(0, 3);
  const lastPart: string = document.substring(document.length - 3);
  const maskedPart: string = '*'.repeat(document.length - 6);
  return `${firstPart}${maskedPart}${lastPart}`;
}

/**
 * Applies CPF formatting mask to a numeric string
 * Formats numbers into CPF pattern: 000.000.000-00
 *
 * @param value - The numeric string to format
 * @returns Formatted CPF string
 *
 * @example
 * formatCpf("12345678900") // Returns "123.456.789-00"
 */
export function formatCpf(value: string): string {
  const onlyNumbers = value.replace(/\D/g, '');
  const limitedNumbers = onlyNumbers.slice(0, 11);
  let maskedValue = '';
  let maskIndex = 0;

  for (let i = 0; i < limitedNumbers.length; i++) {
    if (/\D/.test(CPF_MASK_FORMAT[maskIndex])) {
      maskedValue += CPF_MASK_FORMAT[maskIndex];
      maskIndex++;
    }

    maskedValue += limitedNumbers[i];
    maskIndex++;
  }

  return maskedValue;
}

/**
 * Masks an email address for privacy
 * Shows first 4 characters (or 2 if short) + ****** + @domain
 *
 * @param email - The email address to mask
 * @returns Masked email string
 *
 * @example
 * maskEmail("john.doe@example.com") // Returns "john******@example.com"
 */
export function maskEmail(email: string): string {
  const [localPart, domain] = email.split('@');

  if (!localPart || !domain) {
    return email;
  }

  let visiblePart = '';
  if (localPart.length >= 4) {
    visiblePart = localPart.substring(0, 4);
  } else {
    visiblePart = localPart.substring(0, 2);
  }

  const maskedPart = '******';
  return `${visiblePart}${maskedPart}@${domain}`;
}

/**
 * Auto-detects and masks either email or document
 * Uses email masking if @ is present, otherwise uses CPF formatting
 *
 * @param value - The value to mask (email or document)
 * @returns Masked string
 *
 * @example
 * maskValue("user@example.com") // Returns "user******@example.com"
 * maskValue("12345678900") // Returns "123.456.789-00"
 */
export function maskValue(value: string): string {
  if (value.includes('@')) {
    return maskEmail(value);
  }
  return formatCpf(value);
}

// Export legacy name for backward compatibility
export const secureMaskDocument = maskDocument;
